import React, { useState } from 'react';

import { getObjectSumValues } from 'utilitys/helpers/general';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { useMutationBulkDeleteAction } from 'api/mutations/bulkDeleteAction.mutation';
import { ICountersDataResponse } from 'api/queries/battleCards.query';

import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';
import { ConfirmSaveModal } from 'components/common';

import classes from './bulkDeleteAction.module.scss';

interface Props {
    isShowModal: boolean;
    handleModalClose: () => void;
    cardsCounters?: Record<keyof ICountersDataResponse, number>;
    setIsBulkLoading: React.Dispatch<React.SetStateAction<boolean>>;
    searchedFilterData: any;
    bulkDeleteCardsUniqIdNotification: number;
}

export const BulkDeleteAction = ({
    isShowModal,
    handleModalClose,
    cardsCounters,
    setIsBulkLoading,
    searchedFilterData,
    bulkDeleteCardsUniqIdNotification,
}: Props) => {
    const [confirmModal, setConfirmModal] = useState<boolean>(false);
    const { handleSnackBar } = useCustomSnackBar();
    const { mutate } = useMutationBulkDeleteAction();

    const { sendEventWithDimensions } = useEventWithDimensions();

    const handleConfirmModal = () => {
        if (getObjectSumValues(cardsCounters) === 0) {
            handleSnackBar('You cannot run a bulk delete on zero activities. Update your filter.', 'warning');
        } else {
            setConfirmModal(true);
        }
    };

    const handleDeleteAction = () => {
        const params = {
            ...searchedFilterData,
            uniqIdNotification: bulkDeleteCardsUniqIdNotification,
        };

        mutate(params, {
            onSuccess: () => {
                setIsBulkLoading(true);
                sendEventWithDimensions({
                    category: 'Battlecards',
                    action: 'Bulk Delete',
                    label: `filter: ${JSON.stringify(params.filter)}`,
                });
            },
            onError: (error) => {
                const message = 'Bulk request Failed';
                DEFAULT_ERROR({ error, message, handleError: handleSnackBar });
            },
            onSettled: () => {
                closeModal();
            },
        });
    };

    const closeModal = () => {
        handleModalClose();
        setConfirmModal(false);
    };

    return (
        <>
            <Modal
                open={isShowModal}
                onClose={closeModal}
                title={'Bulk Delete - User Actions & Recommendations'}
                subtitle={'Delete User Actions & Recommendations matching the current filter'}
                actions={
                    <>
                        <Button size={'large'} variant={'secondary'} onClick={closeModal}>
                            Cancel
                        </Button>

                        <Button size={'large'} variant={'primary'} destructive={true} onClick={handleConfirmModal}>
                            Delete
                        </Button>
                    </>
                }
            >
                <div className={classes.bulkDeleteActionContent}>
                    <div className={classes.note}>(Note only User Actions / Recommendations will be deleted)</div>
                </div>
            </Modal>

            <ConfirmSaveModal
                isShowModal={confirmModal}
                onHideModal={() => setConfirmModal(false)}
                onConfirm={handleDeleteAction}
                actionButtonLabel={'Yes'}
            >
                Are you sure you want to delete {getObjectSumValues(cardsCounters).toLocaleString()} battlecards?
            </ConfirmSaveModal>
        </>
    );
};
